/**
 * QuoteEditController — page /devis-edition.html (EPIC-012 / 12.6).
 *
 * Deux modes :
 *  - Édition (`?id=<uuid>`) : charge un devis existant via GET /quotes/{id},
 *    auto-save toutes les 8s via PATCH /quotes/{id} (uniquement si
 *    `status === 'draft'` et `isLocked === false`).
 *  - Création (sans id) : champs vides. Au premier auto-save / sauvegarde
 *    manuelle, POST /quotes/draft pour créer le brouillon, puis bascule en
 *    mode édition (history.replaceState).
 *
 * Lecture seule (`is_locked = true`) : tous les inputs désactivés + bandeau
 * d'alerte rouge.
 *
 * Lignes dynamiques : <template id="tpl-quote-line-row"> + cloneTemplate +
 * setAttribute côté JS (zéro HTML inline — cf. CLAUDE.md).
 *
 * Activité par défaut : activité `principal` de l'org (auto-détectée via
 * GET /activities). Le sélecteur n'est pas exposé en V1 (cohérent avec
 * la maquette qui ne le montre pas) — pour les lignes du catalogue, le
 * backend verrouille `activityId` côté article.
 *
 * Le bouton « Émettre et envoyer » déclenche la transition `issue` puis
 * `send` (en chaîne). 503 / 422 / 423 rendus en message inline.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { formatDate } from "/js/utils/format.js";

const AUTOSAVE_DELAY_MS = 8000;

export default class QuoteEditController extends BaseController {
  static targets = [
    "crumbNumber",
    "overline",
    "title",
    "statusPill",
    "statusLabel",
    "autosaveLabel",
    "lockedBanner",
    "formFieldset",
    "clientCombobox",
    "clientInput",
    "clientIdInput",
    "clientHint",
    "clientError",
    "createClientBtn",
    "referenceField",
    "referenceDisplay",
    "dateCreatedDisplay",
    "dateValidityDisplay",
    "dateValidityInput",
    "dateValidityHint",
    "linesContainer",
    "addLineBtn",
    "discountEnabled",
    "discountFields",
    "discountPercentInput",
    "discountLabelInput",
    "discountRow",
    "discountRowPercent",
    "discountRowLabel",
    "discountRowAmount",
    "subtotalHt",
    "totalHt",
    "totalTtc",
    "saveDraftBtn",
    "issueBtn",
    "previewBtn",
    "archiveBtn",
    "archiveModal",
    "archiveError",
    "archiveConfirmBtn",
    "attachmentsList",
    "parentQuoteBadge",
    "catalogToggleBtn",
    "catalogPicker",
    "catalogCombobox",
    "catalogInput",
    "catalogIdInput",
    "catalogAddBtn",
    "catalogHint",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    this._quoteId = params.get("id");
    this._parentQuoteIdFromUrl = params.get("parent");
    this._parentQuoteId = null;
    this._autosaveTimer = null;
    this._lastSavedAt = null;
    this._lines = [];
    this._defaultActivityId = null;
    this._documents = [];
    this._isLocked = false;
    this._currentStatus = "draft";
    this._selectedCatalogArticle = null;
    this._loadInitialData();
  }

  disconnect() {
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
  }

  async _loadInitialData() {
    try {
      // Le sélecteur client est désormais une combobox `searchable-select` en
      // mode `remote` (EPIC-099.50) : elle interroge `GET /clients?q=` à la
      // frappe, plus besoin de précharger la liste complète ici.
      const [activities, documentsResp] = await Promise.all([
        api.listActivities(),
        api.listDocuments(),
      ]);
      const acts = Array.isArray(activities?.activities) ? activities.activities : [];
      const principal = acts.find((a) => a.role === "principal" && a.status === "active");
      this._defaultActivityId = principal?.id ?? acts[0]?.id ?? null;
      this._documents = Array.isArray(documentsResp?.documents) ? documentsResp.documents : [];
      this._renderAttachments([]);
      if (this._quoteId) {
        await this._loadExistingQuote();
      } else if (this._parentQuoteIdFromUrl) {
        await this._loadParentForComplement(this._parentQuoteIdFromUrl);
      } else {
        this._enterCreationMode();
      }
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les données nécessaires à l'édition.";
      this.showError(msg);
    }
  }

  /**
   * Sélection d'un client depuis la combobox `searchable-select`
   * (événement `searchable-select:change`). Met à jour l'aide (SIRET) et
   * déclenche l'auto-sauvegarde. Une sélection effacée (`id` vide) restaure le
   * libellé d'aide par défaut.
   */
  onClientSelected(event) {
    const { id, item } = event.detail ?? {};
    if (this.hasClientHintTarget) {
      this.clientHintTarget.textContent = item?.siret
        ? `SIRET ${item.siret}`
        : "Saisissez le nom ou le SIRET du client destinataire.";
    }
    if (id) this.scheduleAutoSave();
  }

  /**
   * Pré-remplit la combobox client en mode édition : libellé visible +
   * input caché (id). Le nom légal n'est pas porté par la réponse devis — on
   * le récupère via `getClient`, comme la page de détail.
   */
  async _prefillClient(clientId) {
    if (!clientId || !this.hasClientInputTarget || !this.hasClientIdInputTarget) {
      return;
    }
    this.clientIdInputTarget.value = clientId;
    try {
      const client = await api.getClient(clientId);
      this.clientInputTarget.value = client?.legalName ?? "";
      if (this.hasClientHintTarget && client?.siret) {
        this.clientHintTarget.textContent = `SIRET ${client.siret}`;
      }
    } catch {
      // Best-effort : l'id reste rattaché même si le libellé n'a pu être chargé.
    }
  }

  async _loadExistingQuote() {
    const quote = await api.getQuote(this._quoteId);
    this._isLocked = !!quote.isLocked;
    this._currentStatus = quote.status;
    this._renderHead(quote);
    await this._prefillClient(quote.clientId);
    this._renderReference(quote.number);
    this._renderDatesEditMode(quote);
    if (quote.discountPercent) {
      if (this.hasDiscountEnabledTarget) this.discountEnabledTarget.checked = true;
      if (this.hasDiscountFieldsTarget) this.discountFieldsTarget.hidden = false;
      if (this.hasDiscountPercentInputTarget) {
        this.discountPercentInputTarget.value = quote.discountPercent;
      }
      if (this.hasDiscountLabelInputTarget) {
        this.discountLabelInputTarget.value = quote.discountLabel ?? "";
      }
    }
    this._lines = Array.isArray(quote.lines) ? quote.lines.map((l) => ({ ...l })) : [];
    this._renderLines();
    this._renderAttachments(Array.isArray(quote.attachedDocumentIds) ? quote.attachedDocumentIds : []);
    this.recomputeTotals();
    this._applyLockedState();
    if (this.hasArchiveBtnTarget) {
      this.archiveBtnTarget.hidden = quote.status !== "draft" || this._isLocked;
    }
    if (quote.parentQuoteId) {
      this._applyParentLock(quote.parentQuote ?? { id: quote.parentQuoteId, number: "—" });
    }
  }

  async _loadParentForComplement(parentId) {
    let parent;
    try {
      const resp = await api.getQuote(parentId);
      parent = resp.quote ?? resp;
    } catch (error) {
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Devis parent introuvable.";
      this.showError(msg);
      window.location.assign("/devis-edition.html");
      return;
    }
    if (parent.clientId) {
      await this._prefillClient(parent.clientId);
    }
    this._applyParentLock({
      id: parent.id,
      number: parent.number ?? "—",
    });
    this._enterCreationMode();
  }

  _applyParentLock(parent) {
    this._parentQuoteId = parent.id;
    // Client non modifiable : on verrouille la saisie de la combobox et on
    // masque le raccourci « Créer un client ».
    if (this.hasClientInputTarget) {
      this.clientInputTarget.disabled = true;
    }
    if (this.hasCreateClientBtnTarget) {
      this.createClientBtnTarget.hidden = true;
    }
    if (this.hasClientHintTarget) {
      this.clientHintTarget.textContent = "Client du devis parent — non modifiable.";
    }
    if (this.hasParentQuoteBadgeTarget) {
      const numberSlot = this.parentQuoteBadgeTarget.querySelector('[data-slot="parentQuoteNumber"]');
      const linkEl = this.parentQuoteBadgeTarget.querySelector('[data-slot-element="parentQuoteLink"]');
      if (numberSlot) numberSlot.textContent = parent.number ?? "—";
      if (linkEl && parent.id) {
        linkEl.setAttribute("href", `/devis-detail.html?id=${encodeURIComponent(parent.id)}`);
      }
      this.parentQuoteBadgeTarget.hidden = false;
    }
  }

  /**
   * Référence devis : champ affiché uniquement quand un numéro existe
   * (attribué à l'émission). En création / brouillon non numéroté, masqué —
   * un champ vide non éditable n'apporte rien.
   */
  _renderReference(number) {
    if (!this.hasReferenceFieldTarget) return;
    if (number) {
      this.referenceFieldTarget.hidden = false;
      if (this.hasReferenceDisplayTarget) this.referenceDisplayTarget.textContent = number;
    } else {
      this.referenceFieldTarget.hidden = true;
    }
  }

  /**
   * Mode modification (le devis a un id) : la date d'émission s'affiche en
   * texte, la date de validité devient un champ éditable pré-rempli.
   */
  _renderDatesEditMode(quote) {
    if (this.hasDateCreatedDisplayTarget) {
      this.dateCreatedDisplayTarget.textContent = formatDate(quote.dateCreated, { emptyValue: "—" });
    }
    if (this.hasDateValidityDisplayTarget) this.dateValidityDisplayTarget.hidden = true;
    if (this.hasDateValidityInputTarget) {
      this.dateValidityInputTarget.hidden = false;
      this.dateValidityInputTarget.value = quote.dateValidity ?? "";
    }
    if (this.hasDateValidityHintTarget) {
      this.dateValidityHintTarget.textContent = "30 jours après émission par défaut.";
    }
  }

  _enterCreationMode() {
    this._lines = [];
    this._renderLines();
    // Mode création : aperçu des dates en lecture seule. Émission = aujourd'hui,
    // validité = aujourd'hui + 30 j (valeurs définitives posées par le backend à
    // l'enregistrement). La validité ne devient éditable qu'ensuite.
    const today = new Date();
    const validity = new Date(today);
    validity.setDate(validity.getDate() + 30);
    if (this.hasDateCreatedDisplayTarget) this.dateCreatedDisplayTarget.textContent = formatDate(today);
    if (this.hasDateValidityDisplayTarget) {
      this.dateValidityDisplayTarget.textContent = formatDate(validity);
      this.dateValidityDisplayTarget.hidden = false;
    }
    if (this.hasDateValidityInputTarget) this.dateValidityInputTarget.hidden = true;
    if (this.hasDateValidityHintTarget) {
      this.dateValidityHintTarget.textContent =
        "La date de validité sera modifiable après l'enregistrement du brouillon.";
    }
    if (this.hasOverlineTarget) this.overlineTarget.textContent = "Nouveau brouillon";
    if (this.hasTitleTarget) this.titleTarget.textContent = "Brouillon (non enregistré)";
    if (this.hasAutosaveLabelTarget) {
      this.autosaveLabelTarget.textContent = "Non sauvegardé — sélectionnez un client puis « Enregistrer en brouillon »";
    }
    if (this.hasIssueBtnTarget) this.issueBtnTarget.disabled = true;
  }

  _renderHead(quote) {
    const number = quote.number ?? "Brouillon";
    if (this.hasCrumbNumberTarget) this.crumbNumberTarget.textContent = number;
    if (this.hasTitleTarget) this.titleTarget.textContent = number;
    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent = quote.status === "draft" ? "Brouillon" : "Devis émis";
    }
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = this._statusFrenchLabel(quote.status);
    }
    if (this.hasLockedBannerTarget) this.lockedBannerTarget.hidden = !this._isLocked;
  }

  _statusFrenchLabel(status) {
    const map = {
      draft: "brouillon",
      issued: "émis",
      sent: "envoyé",
      accepted: "accepté",
      refused: "refusé",
      partially_invoiced: "partiellement facturé",
      fully_invoiced: "facturé",
      expired: "expiré",
      archived: "archivé",
    };
    return map[status] ?? status;
  }

  _applyLockedState() {
    if (!this.hasFormFieldsetTarget) return;
    const disabled = this._isLocked || (this._currentStatus !== "draft" && this._quoteId);
    const inputs = this.element.querySelectorAll("input:not([readonly]), select, textarea, button[type='button']:not([data-quote-edit-target='previewBtn']):not([data-action*='closeArchiveDialog']):not([data-action*='openArchiveDialog'])");
    inputs.forEach((el) => {
      if (el.closest("dialog")) return;
      el.disabled = disabled;
    });
    if (this.hasIssueBtnTarget) this.issueBtnTarget.disabled = disabled;
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = disabled;
    if (this.hasAutosaveLabelTarget && disabled) {
      this.autosaveLabelTarget.textContent = this._isLocked
        ? "Modification désactivée — devis verrouillé."
        : "Modification désactivée — devis émis.";
    }
    if (this.hasAddLineBtnTarget) this.addLineBtnTarget.disabled = disabled;
  }

  // ---------------------------------------------------------------------------
  // Lines
  // ---------------------------------------------------------------------------

  _renderLines() {
    if (!this.hasLinesContainerTarget) return;
    while (this.linesContainerTarget.firstChild) {
      this.linesContainerTarget.removeChild(this.linesContainerTarget.firstChild);
    }
    if (this._lines.length === 0) {
      this._addLineRow({});
      return;
    }
    for (const line of this._lines) {
      this._addLineRow(line);
    }
  }

  addLine() {
    this._addLineRow({});
    this.scheduleAutoSave();
  }

  _addLineRow(line) {
    if (!this.hasLinesContainerTarget) return;
    const idx = this.linesContainerTarget.children.length + 1;
    const fragment = this.cloneTemplate("tpl-quote-line-row");
    const ids = {
      descId: `l${idx}-desc`,
      refId: `l${idx}-ref`,
      qtyId: `l${idx}-qty`,
      puId: `l${idx}-pu`,
      totId: `l${idx}-tot`,
      deleteLabel: `Supprimer la ligne ${idx}`,
    };
    fragment.querySelectorAll("[data-slot-attr-for]").forEach((el) => {
      const key = el.dataset.slotAttrFor;
      if (ids[key]) el.setAttribute("for", ids[key]);
    });
    fragment.querySelectorAll("[data-slot-attr-id]").forEach((el) => {
      const key = el.dataset.slotAttrId;
      if (ids[key]) el.setAttribute("id", ids[key]);
    });
    fragment.querySelectorAll("[data-slot-attr-aria-label]").forEach((el) => {
      const key = el.dataset.slotAttrAriaLabel;
      if (ids[key]) el.setAttribute("aria-label", ids[key]);
    });
    const labelInput = fragment.querySelector('[data-slot-element="labelInput"]');
    const refInput = fragment.querySelector('[data-slot-element="referenceInput"]');
    const qtyInput = fragment.querySelector('[data-slot-element="quantityInput"]');
    const priceInput = fragment.querySelector('[data-slot-element="priceInput"]');
    const totalInput = fragment.querySelector('[data-slot-element="totalInput"]');
    if (labelInput) labelInput.value = line.label ?? "";
    if (refInput) refInput.value = line.reference ?? "";
    if (qtyInput) qtyInput.value = line.quantity ?? "1";
    if (priceInput) priceInput.value = line.priceExclVat ?? "0";
    if (totalInput) {
      const q = Number(line.quantity ?? "1");
      const p = Number(line.priceExclVat ?? "0");
      totalInput.value = Number.isFinite(q * p) ? (q * p).toFixed(2) : "0.00";
    }
    const row = fragment.querySelector("[data-quote-edit-line]");
    if (row && line.articleId) {
      row.dataset.articleId = line.articleId;
      if (refInput) {
        refInput.readOnly = true;
        refInput.style.background = "var(--surface)";
      }
    }
    if (row && line.activityId) row.dataset.activityId = line.activityId;
    this.linesContainerTarget.appendChild(fragment);
  }

  deleteLine(event) {
    const row = event.currentTarget.closest("[data-quote-edit-line]");
    if (!row) return;
    row.remove();
    this.recomputeTotals();
    this.scheduleAutoSave();
  }

  // ---------------------------------------------------------------------------
  // Catalogue — ajout d'une ligne depuis un article du catalogue
  // ---------------------------------------------------------------------------

  toggleCatalogPicker() {
    if (!this.hasCatalogPickerTarget) return;
    const willShow = this.catalogPickerTarget.hidden;
    this.catalogPickerTarget.hidden = !willShow;
    if (this.hasCatalogToggleBtnTarget) {
      this.catalogToggleBtnTarget.setAttribute("aria-expanded", String(willShow));
    }
    // La combobox catalogue (mode remote) interroge le catalogue à la frappe ;
    // aucun préchargement nécessaire. Le bouton « Ajouter » reste désactivé
    // tant qu'aucun article n'est sélectionné.
    if (willShow && this.hasCatalogAddBtnTarget) {
      this.catalogAddBtnTarget.disabled = !this._selectedCatalogArticle;
      if (this.hasCatalogInputTarget) this.catalogInputTarget.focus();
    }
  }

  /**
   * Sélection d'un article via la combobox catalogue
   * (événement `searchable-select:change`). Mémorise l'article complet pour
   * `addCatalogLine` et (dé)active le bouton d'ajout.
   */
  onCatalogArticleSelected(event) {
    const { id, item } = event.detail ?? {};
    this._selectedCatalogArticle = id ? item : null;
    if (this.hasCatalogAddBtnTarget) {
      this.catalogAddBtnTarget.disabled = !this._selectedCatalogArticle;
    }
  }

  addCatalogLine() {
    const article = this._selectedCatalogArticle;
    if (!article) return;
    this._fillFirstEmptyLine({
      label: article.label,
      reference: article.reference ?? "",
      quantity: "1",
      priceExclVat: article.priceExclVat ?? "0",
      articleId: article.id,
      activityId: article.activityId ?? null,
    });
    this.recomputeTotals();
    this.scheduleAutoSave();
    // Réinitialise la combobox pour enchaîner un autre ajout (§06 l.11).
    this._selectedCatalogArticle = null;
    this._resetCombobox(this.hasCatalogComboboxTarget ? this.catalogComboboxTarget : null);
    if (this.hasCatalogAddBtnTarget) this.catalogAddBtnTarget.disabled = true;
  }

  /**
   * §06 l.11 — un article choisi remplit la **première ligne vide** ; s'il n'y
   * en a pas, on ajoute une nouvelle ligne. Une ligne est « vide » quand son
   * libellé est vide et qu'elle n'est pas déjà rattachée à un article.
   */
  _fillFirstEmptyLine(line) {
    const emptyRow = this._findFirstEmptyLineRow();
    if (!emptyRow) {
      this._addLineRow(line);
      return;
    }
    const labelInput = emptyRow.querySelector('[data-slot-element="labelInput"]');
    const refInput = emptyRow.querySelector('[data-slot-element="referenceInput"]');
    const qtyInput = emptyRow.querySelector('[data-slot-element="quantityInput"]');
    const priceInput = emptyRow.querySelector('[data-slot-element="priceInput"]');
    if (labelInput) labelInput.value = line.label ?? "";
    if (refInput) {
      refInput.value = line.reference ?? "";
      if (line.articleId) {
        refInput.readOnly = true;
        refInput.style.background = "var(--surface)";
      }
    }
    if (qtyInput) qtyInput.value = line.quantity ?? "1";
    if (priceInput) priceInput.value = line.priceExclVat ?? "0";
    if (line.articleId) emptyRow.dataset.articleId = line.articleId;
    if (line.activityId) emptyRow.dataset.activityId = line.activityId;
  }

  _findFirstEmptyLineRow() {
    if (!this.hasLinesContainerTarget) return null;
    const rows = this.linesContainerTarget.querySelectorAll("[data-quote-edit-line]");
    for (const row of rows) {
      if (row.dataset.articleId) continue;
      const label = row.querySelector('[data-slot-element="labelInput"]')?.value ?? "";
      if (label.trim() === "") return row;
    }
    return null;
  }

  /**
   * Réinitialise une combobox `searchable-select` (libellé + id) via son
   * contrôleur Stimulus.
   */
  _resetCombobox(comboboxEl) {
    if (!comboboxEl) return;
    const ctrl = this.application.getControllerForElementAndIdentifier(
      comboboxEl,
      "searchable-select",
    );
    if (ctrl?.clear) ctrl.clear();
  }

  // ---------------------------------------------------------------------------
  // Totals
  // ---------------------------------------------------------------------------

  recomputeTotals() {
    if (!this.hasLinesContainerTarget) return;
    let subtotal = 0;
    const rows = this.linesContainerTarget.querySelectorAll("[data-quote-edit-line]");
    rows.forEach((row) => {
      const qty = Number(row.querySelector('[data-slot-element="quantityInput"]')?.value);
      const price = Number(row.querySelector('[data-slot-element="priceInput"]')?.value);
      const totalInput = row.querySelector('[data-slot-element="totalInput"]');
      const lineTotal = Number.isFinite(qty) && Number.isFinite(price) ? qty * price : 0;
      if (totalInput) totalInput.value = lineTotal.toFixed(2);
      subtotal += lineTotal;
    });
    const discountEnabled = this.hasDiscountEnabledTarget && this.discountEnabledTarget.checked;
    const discountPct = discountEnabled && this.hasDiscountPercentInputTarget
      ? Number(this.discountPercentInputTarget.value)
      : 0;
    const discountAmount = Number.isFinite(discountPct) && discountPct > 0
      ? (subtotal * discountPct) / 100
      : 0;
    const totalHt = subtotal - discountAmount;
    if (this.hasSubtotalHtTarget) this.subtotalHtTarget.textContent = `${this._formatMoney(subtotal)} €`;
    if (this.hasTotalHtTarget) this.totalHtTarget.textContent = `${this._formatMoney(totalHt)} €`;
    if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = `${this._formatMoney(totalHt)} €`;
    if (this.hasDiscountRowTarget) {
      if (discountEnabled && discountPct > 0) {
        this.discountRowTarget.hidden = false;
        if (this.hasDiscountRowPercentTarget) {
          this.discountRowPercentTarget.textContent = this._formatMoney(discountPct);
        }
        if (this.hasDiscountRowLabelTarget && this.hasDiscountLabelInputTarget) {
          this.discountRowLabelTarget.textContent = this.discountLabelInputTarget.value ?? "";
        }
        if (this.hasDiscountRowAmountTarget) {
          this.discountRowAmountTarget.textContent = `−${this._formatMoney(discountAmount)} €`;
        }
      } else {
        this.discountRowTarget.hidden = true;
      }
    }
  }

  toggleDiscount() {
    if (!this.hasDiscountFieldsTarget || !this.hasDiscountEnabledTarget) return;
    this.discountFieldsTarget.hidden = !this.discountEnabledTarget.checked;
    if (!this.discountEnabledTarget.checked) {
      if (this.hasDiscountPercentInputTarget) this.discountPercentInputTarget.value = "";
      if (this.hasDiscountLabelInputTarget) this.discountLabelInputTarget.value = "";
    }
    this.recomputeTotals();
    this.scheduleAutoSave();
  }

  // ---------------------------------------------------------------------------
  // Attachments
  // ---------------------------------------------------------------------------

  _renderAttachments(selectedIds) {
    if (!this.hasAttachmentsListTarget) return;
    while (this.attachmentsListTarget.firstChild) {
      this.attachmentsListTarget.removeChild(this.attachmentsListTarget.firstChild);
    }
    if (this._documents.length === 0) {
      const li = document.createElement("li");
      li.className = "is-muted fs-13";
      li.style.padding = "12px 0";
      li.textContent = "Aucun document disponible.";
      this.attachmentsListTarget.appendChild(li);
      return;
    }
    for (const doc of this._documents) {
      const fragment = this.cloneTemplate("tpl-attachment-row");
      const checkboxId = `att-${doc.id}`;
      fragment.querySelectorAll("[data-slot-attr-for]").forEach((el) => {
        if (el.dataset.slotAttrFor === "checkboxId") el.setAttribute("for", checkboxId);
      });
      fragment.querySelectorAll("[data-slot-attr-id]").forEach((el) => {
        if (el.dataset.slotAttrId === "checkboxId") el.setAttribute("id", checkboxId);
      });
      fragment.querySelectorAll("[data-slot-attr-value]").forEach((el) => {
        if (el.dataset.slotAttrValue === "documentId") el.setAttribute("value", doc.id);
      });
      this.fillSlots(fragment, {
        name: doc.name ?? "—",
        meta: this._documentMeta(doc),
      });
      const checkbox = fragment.querySelector('[data-slot-element="checkbox"]');
      if (checkbox && selectedIds.includes(doc.id)) {
        checkbox.checked = true;
      }
      this.attachmentsListTarget.appendChild(fragment);
    }
  }

  _documentMeta(doc) {
    const parts = [];
    if (doc.category) parts.push(doc.category);
    if (doc.fileSizeBytes) parts.push(`${Math.round(doc.fileSizeBytes / 1024)} Ko`);
    return parts.join(" · ") || "—";
  }

  // ---------------------------------------------------------------------------
  // Auto-save / explicit save
  // ---------------------------------------------------------------------------

  scheduleAutoSave() {
    if (this._isLocked || this._currentStatus !== "draft") return;
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    if (this.hasAutosaveLabelTarget) {
      this.autosaveLabelTarget.textContent = "Modifications non sauvegardées…";
    }
    this._autosaveTimer = setTimeout(() => this._performSave(false), AUTOSAVE_DELAY_MS);
  }

  async saveDraftNow() {
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    await this._performSave(true);
  }

  async _performSave(explicit) {
    const payload = this._buildPayload();
    if (!payload) {
      if (this.hasAutosaveLabelTarget) {
        this.autosaveLabelTarget.textContent = "Sélectionnez un client pour pouvoir enregistrer.";
      }
      return;
    }
    try {
      let quote;
      if (this._quoteId) {
        quote = await api.updateQuote(this._quoteId, payload);
      } else {
        quote = await api.createQuote(payload);
        this._quoteId = quote.id;
        window.history.replaceState(
          null,
          "",
          `/devis-edition.html?id=${encodeURIComponent(quote.id)}`,
        );
        if (this.hasIssueBtnTarget) this.issueBtnTarget.disabled = false;
        // Le brouillon existe désormais : on passe en mode modification (date
        // d'émission renseignée, validité éditable).
        this._renderDatesEditMode(quote);
      }
      this._lastSavedAt = new Date();
      if (this.hasAutosaveLabelTarget) {
        this.autosaveLabelTarget.textContent = `Auto-sauvegardé à ${this._lastSavedAt.toLocaleTimeString("fr-FR")}`;
      }
      if (explicit) this.showSuccess("Brouillon enregistré.");
      this._renderReference(quote.number);
    } catch (error) {
      const msg = error instanceof APIError ? error.message : "Erreur lors de l'enregistrement.";
      if (this.hasAutosaveLabelTarget) {
        this.autosaveLabelTarget.textContent = `Erreur d'auto-sauvegarde : ${msg}`;
      }
      if (explicit) this.showError(msg);
    }
  }

  _buildPayload() {
    if (!this.hasClientIdInputTarget) return null;
    const clientId = this.clientIdInputTarget.value;
    if (!clientId) return null;
    const lines = [];
    if (this.hasLinesContainerTarget) {
      const rows = this.linesContainerTarget.querySelectorAll("[data-quote-edit-line]");
      rows.forEach((row, idx) => {
        const label = row.querySelector('[data-slot-element="labelInput"]')?.value ?? "";
        const reference = row.querySelector('[data-slot-element="referenceInput"]')?.value ?? "";
        const quantity = row.querySelector('[data-slot-element="quantityInput"]')?.value ?? "0";
        const priceExclVat = row.querySelector('[data-slot-element="priceInput"]')?.value ?? "0";
        if (label.trim() === "") return;
        const entry = {
          label,
          quantity: Number(quantity).toFixed(2),
          unit: "unite",
          priceExclVat: Number(priceExclVat).toFixed(2),
          vatRate: "0",
          lineIndex: idx,
        };
        if (reference) entry.reference = reference;
        const articleId = row.dataset.articleId;
        if (articleId) entry.articleId = articleId;
        else if (this._defaultActivityId) entry.activityId = this._defaultActivityId;
        lines.push(entry);
      });
    }
    const payload = {
      clientId,
      lines,
    };
    if (this.hasDateValidityInputTarget && this.dateValidityInputTarget.value) {
      payload.dateValidity = this.dateValidityInputTarget.value;
    }
    if (this.hasDiscountEnabledTarget && this.discountEnabledTarget.checked) {
      const pct = this.discountPercentInputTarget?.value;
      if (pct) payload.discountPercent = Number(pct).toFixed(2);
      const lbl = this.discountLabelInputTarget?.value;
      if (lbl) payload.discountLabel = lbl;
    }
    const attachmentIds = [];
    if (this.hasAttachmentsListTarget) {
      this.attachmentsListTarget.querySelectorAll('input[type="checkbox"]:checked').forEach((cb) => {
        if (cb.value) attachmentIds.push(cb.value);
      });
    }
    payload.attachedDocumentIds = attachmentIds;
    if (this._parentQuoteId) {
      payload.parentQuoteId = this._parentQuoteId;
    }
    return payload;
  }

  // ---------------------------------------------------------------------------
  // Issue & send
  // ---------------------------------------------------------------------------

  async submit(event) {
    event.preventDefault();
    if (!this._quoteId) {
      await this.saveDraftNow();
      if (!this._quoteId) return;
    } else {
      // S'assurer que les dernières modifs sont synchronisées.
      await this._performSave(false);
    }
    try {
      if (this.hasIssueBtnTarget) this.issueBtnTarget.disabled = true;
      await api.issueQuote(this._quoteId);
      await api.sendQuote(this._quoteId, {});
      this.showSuccess("Devis émis et envoyé au client.");
      window.location.assign(`/devis-detail.html?id=${encodeURIComponent(this._quoteId)}`);
    } catch (error) {
      const msg = error instanceof APIError ? error.message : "Erreur lors de l'émission.";
      this.showError(msg);
      if (this.hasIssueBtnTarget) this.issueBtnTarget.disabled = false;
    }
  }

  async previewPdf() {
    if (!this._quoteId) {
      this.showError("Enregistrez d'abord le brouillon avant d'en générer un aperçu.");
      return;
    }
    const url = `${window.location.origin.replace(/\/$/, "")}` +
      `/quotes/${encodeURIComponent(this._quoteId)}/preview-pdf`;
    window.open(url, "_blank", "noopener");
  }

  // ---------------------------------------------------------------------------
  // Archive dialog
  // ---------------------------------------------------------------------------

  openArchiveDialog() {
    if (!this.hasArchiveModalTarget) return;
    if (this.hasArchiveErrorTarget) this.archiveErrorTarget.hidden = true;
    if (this.hasArchiveConfirmBtnTarget) this.archiveConfirmBtnTarget.disabled = false;
    this.archiveModalTarget.showModal();
  }

  closeArchiveDialog() {
    if (this.hasArchiveModalTarget) this.archiveModalTarget.close();
  }

  async confirmArchive() {
    if (!this._quoteId) return;
    if (this.hasArchiveConfirmBtnTarget) this.archiveConfirmBtnTarget.disabled = true;
    try {
      await api.archiveQuote(this._quoteId);
      this.showSuccess("Brouillon archivé.");
      window.location.assign("/devis.html");
    } catch (error) {
      const msg = error instanceof APIError ? error.message : "Erreur lors de l'archivage.";
      if (this.hasArchiveErrorTarget) {
        this.archiveErrorTarget.textContent = msg;
        this.archiveErrorTarget.hidden = false;
      }
      if (this.hasArchiveConfirmBtnTarget) this.archiveConfirmBtnTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatMoney(raw) {
    const num = Number(raw);
    if (!Number.isFinite(num)) return "0,00";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
